import { useRef, useState } from "react";
import { Send, Volume2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { API, useApi } from "@/lib/api";
import { speak, stopSpeaking } from "@/lib/speech";
import { useT } from "@/lib/i18n";
import { AgentAvatar } from "@/components/common";

async function streamChat(token, agentId, message, history, onEvent) {
  const res = await fetch(`${API}/agents/${agentId}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ message, history }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Error");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const p of parts) {
      const data = p.replace(/^data: /, "");
      if (data && data !== "[DONE]") onEvent(JSON.parse(data));
    }
  }
}

export default function AgentTestChat({ agent }) {
  const { t } = useT();
  const api = useApi();
  const qc = useQueryClient();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const voiceOpts = { voice: agent.voice, rate: agent.voice_speed, pitch: agent.voice_pitch, lang: agent.language };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    const history = msgs.map((m) => ({ author: m.role === "ai" ? agent.name : "user", text: m.text }));
    setMsgs((m) => [...m, { role: "user", text }, { role: "ai", text: "", sources: [] }]);
    setText("");
    setBusy(true);
    const patch = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    let full = "";
    try {
      await streamChat(await api.getToken(), agent.id, text, history, (ev) => {
        if (ev.sources) patch((x) => ({ ...x, sources: ev.sources }));
        if (ev.delta) { full += ev.delta; patch((x) => ({ ...x, text: x.text + ev.delta })); }
        if (ev.error) patch((x) => ({ ...x, text: ev.error, error: true }));
        endRef.current?.scrollIntoView({ behavior: "smooth" });
      });
      if (full) { stopSpeaking(); speak(full, voiceOpts); }
      qc.invalidateQueries({ queryKey: ["/overview"] });
    } catch (err) {
      patch((x) => ({ ...x, text: err.message, error: true }));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="agent-test-chat">
      <div className="flex-1 overflow-y-auto scrollbar-thin space-y-3 py-4 pr-1">
        {msgs.length === 0 && <p className="text-sm text-slate-500">{t("test_chat_hint")}</p>}
        {msgs.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`test-msg-${m.role}-${i}`}>
            {m.role === "ai" && <AgentAvatar agent={agent} size="h-8 w-8" speaking={busy && i === msgs.length - 1} />}
            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.role === "user" ? "bg-white/10" : m.error ? "bg-rose-950/40 text-rose-200" : "bg-indigo-950/40 border border-indigo-500/20"}`}>
              {m.text || <span className="text-slate-500 animate-pulse">…</span>}
              {m.role === "ai" && m.text && !m.error && (
                <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500">
                  <button type="button" onClick={() => { stopSpeaking(); speak(m.text, voiceOpts); }} className="hover:text-white" data-testid={`replay-voice-${i}`}><Volume2 className="h-3.5 w-3.5" /></button>
                  {m.sources?.length > 0 && <span>{t("sources")}: {[...new Set(m.sources)].join(", ")}</span>}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 pt-3 border-t border-white/10">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("ask_agent")} data-testid="test-chat-input" />
        <Button type="submit" disabled={busy} className="bg-indigo-600 hover:bg-indigo-500" data-testid="test-chat-send-btn"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );
}
